import type { ErrorPolicy } from '@apollo/client';
import type { useApolloClient } from '@apollo/client/react';
import type { DocumentNode } from 'graphql';
import { abortableQueryContext } from '../lib/abortable-query-context.js';

export type PagedConnection<TNode> = {
  edges: { node: TNode }[];
  pageInfo: { hasNextPage: boolean; endCursor?: string | null | undefined };
};

type FetchAllPagesOptions<
  TData,
  TVariables extends { first?: number | null; after?: string | null },
  TNode,
> = {
  query: DocumentNode;
  // Every field except `first`/`after` -- this helper drives paging itself.
  variables: Omit<TVariables, 'first' | 'after'>;
  pageSize: number;
  extractConnection: (data: TData) => PagedConnection<TNode> | null | undefined;
  // 'no-cache' skips normalizing exported rows into the session's Apollo cache, which nothing reclaims.
  fetchPolicy?: 'network-only' | 'no-cache';
  // Pass 'all' when a query selects a per-field-gated relation, so one denied field comes back null; a response with no data at all still throws.
  errorPolicy?: ErrorPolicy;
  // Aborting cancels every remaining page request and rejects with the signal's reason, never a partial list.
  signal?: AbortSignal;
  // Stops after the page that takes the list past this many nodes, so `length > maxNodes` means it was cut.
  maxNodes?: number;
};

// A page holds up to 100 rows, so this 1M-row ceiling only trips on a server pagination bug.
const MAX_PAGES = 10_000;

/** Walks a cursor-paginated GraphQL connection until `hasNextPage` is false, returning every page's nodes as one list. */
export async function fetchAllPages<
  TData,
  TVariables extends { first?: number | null; after?: string | null },
  TNode,
>(
  client: ReturnType<typeof useApolloClient>,
  {
    query,
    variables,
    pageSize,
    extractConnection,
    fetchPolicy = 'network-only',
    errorPolicy,
    signal,
    maxNodes = Number.POSITIVE_INFINITY,
  }: FetchAllPagesOptions<TData, TVariables, TNode>,
): Promise<TNode[]> {
  const nodes: TNode[] = [];
  let after: string | null | undefined;
  let hasNextPage = true;
  let pageCount = 0;

  while (hasNextPage && nodes.length <= maxNodes) {
    signal?.throwIfAborted();
    if (pageCount >= MAX_PAGES) throw new Error('fetchAllPages exceeded the page ceiling.');
    const { data, error } = await client.query<TData, TVariables>({
      query,
      variables: { ...variables, first: pageSize, after } as TVariables,
      fetchPolicy,
      ...(errorPolicy ? { errorPolicy } : {}),
      ...(signal ? { context: abortableQueryContext(signal) } : {}),
    });
    if (!data) throw error ?? new Error('fetchAllPages received no data.');
    const connection = extractConnection(data);
    if (!connection) {
      throw error ?? new Error('fetchAllPages: extractConnection returned no connection.');
    }
    // The Relay Connection spec allows a null edge even though PagedConnection's type doesn't.
    if (connection.edges.some((edge) => edge == null)) {
      throw new Error('fetchAllPages: server returned a null edge.');
    }
    nodes.push(...connection.edges.map((edge) => edge.node));
    hasNextPage = connection.pageInfo.hasNextPage;
    after = connection.pageInfo.endCursor;
    if (hasNextPage && !after) throw new Error('fetchAllPages: hasNextPage with no cursor.');
    pageCount += 1;
  }

  return nodes;
}
